import type { GeoEntity } from '@/stores/geolocation-store'

/**
 * Three-way render classification driven entirely by the backend-stamped
 * `mapRenderMode` property. Replaces the previous heuristic-with-regex
 * `isTrackable()` that lumped animated tracks together with time-anchored
 * pins. The split matters because the two render modes deserve different
 * temporal UI:
 *
 *   • `animated_track`   → multi-point trajectory, plays back via scrubber
 *   • `time_anchored_pin`→ single timestamped point, filterable by range
 *   • everything else    → static / time-agnostic
 *
 * Entities whose `mapRenderMode` is `null` are intentionally not rendered
 * (e.g. PhoneNumber) — they fall through to `staticEntities` only because
 * callers may still want to display them in non-map contexts.
 */

export function isAnimated(e: GeoEntity): boolean {
  return e.mapRenderMode === 'animated_track'
}

export function isTimeAnchored(e: GeoEntity): boolean {
  return e.mapRenderMode === 'time_anchored_pin'
}

export function isTimeAware(e: GeoEntity): boolean {
  return isAnimated(e) || isTimeAnchored(e)
}

export interface ClassifiedEntities {
  animated: GeoEntity[]
  timeAnchored: GeoEntity[]
  staticEntities: GeoEntity[]
}

export function classifyEntities(entities: GeoEntity[]): ClassifiedEntities {
  const animated: GeoEntity[] = []
  const timeAnchored: GeoEntity[] = []
  const staticEntities: GeoEntity[] = []
  for (const e of entities) {
    if (isAnimated(e)) animated.push(e)
    else if (isTimeAnchored(e)) timeAnchored.push(e)
    else staticEntities.push(e)
  }
  return { animated, timeAnchored, staticEntities }
}
